import _ from 'lodash';

const initialResume = {
  summary: null,
  location: null,
  education: [],
  employments: [],
  functionalAreas: [],
  industries: [],
  productTechnologies: [],
  therapeuticAreas: [],
  stagesOfDevelopments: [],
};

const initialLinkedIn = {
  summary: null,
  location: null,
  education: [],
  pastRoles: [],
  functionalAreas: [],
  industries: [],
  productTechnologies: [],
  therapeuticAreas: [],
  stagesOfDevelopments: [],
};

const initialState = {
  profileSuggestions: {
    updated: false,
    resume: initialResume,
    linkedin: initialLinkedIn,
    merged: {
      location: {
        accepted: true,
        location: null,
      },
      bio: {
        accepted: true,
        bio: null,
      },
      education: [],
      pastRoles: [],
      functionalAreas: [],
      industries: [],
      productTechnologies: [],
      therapeuticAreas: [],
      stagesOfDevelopments: [],
    },
  },
  errors: {
    is: false,
  },
  answers: {
    location: null,
    bio: null,
    education: [],
    pastRoles: [],
    functionalAreas: [],
    industries: [],
    productTechnologies: [],
    therapeuticAreas: [],
    stagesOfDevelopments: [],
  },
  hasChanged: {
    bio: false,
    location: false,
    education: false,
    pastRoles: false,
    functionalAreas: false,
    industries: false,
    productTechnologies: false,
    therapeuticAreas: false,
    stagesOfDevelopments: false,
  },
};

const toCamel = (str) => str.replace(/([-_][a-z])/ig, ($1) => $1.toUpperCase()
  .replace('-', '')
  .replace('_', ''));

const isObject = (obj) => obj === Object(obj) && !Array.isArray(obj) && typeof obj !== 'function';

const keysToCamel = (obj) => {
  if (isObject(obj)) {
    const n = {};

    Object.keys(obj)
      .forEach((k) => {
        n[toCamel(k)] = keysToCamel(obj[k]);
      });

    return n;
  } if (Array.isArray(obj)) {
    return obj.map((i) => keysToCamel(i));
  }

  return obj;
};

const checkData = (data) => {
  const errors = {
    is: false,
    education: false,
  };

  Object.keys(data).forEach((key) => {
    if (key === 'education') {
      const education = data.education.filter((a) => a.accepted);
      errors.education = Array(education.length).fill(false);
    }
  });

  return errors;
};

const parsingPostTreatment = (mergedValues) => {
  const merged = JSON.parse(JSON.stringify(mergedValues));
  merged.pastRoles = (merged.pastRoles || []).map((pastRole) => (
    _.mapKeys(pastRole, (v, k) => _.camelCase(k))
  ));
  merged.education = (merged.education || []).map((education) => (
    _.mapKeys(education, (v, k) => _.camelCase(k))
  ));
  const pastRoles = (merged.pastRoles || []).map((p) => ({ accepted: true, pastRole: p }));
  const education = (merged.education || []).map((e) => ({ accepted: true, education: e }));
  const { bio } = merged;
  const { location } = merged;
  const functionalAreas = (merged.functionalAreas || []).map(
    (fa) => ({ accepted: true, functionalArea: fa }),
  );
  const therapeuticAreas = (merged.therapeuticAreas || []).map(
    (ta) => ({ accepted: true, therapeuticArea: ta }),
  );
  const industries = (merged.industries || []).map((i) => ({ accepted: true, industrie: i }));
  const productTechnologies = (merged.productTechnologies || []).map(
    (pt) => ({ accepted: true, productTechnologie: pt }),
  );
  const stagesOfDevelopments = (merged.stagesOfDevelopments || []).map(
    (sod) => ({ accepted: true, stagesOfDevelopment: sod }),
  );

  return {
    pastRoles,
    education,
    bio,
    location,
    functionalAreas,
    therapeuticAreas,
    industries,
    productTechnologies,
    stagesOfDevelopments,
  };
};

const arePastRolesChanged = (answered, suggested) => {
  // There are no suggested past roles, so nothing to udpate
  if (suggested.length === 0) {
    return false;
  }

  // Past roles may be overriden because there are some suggestions
  if (suggested.length > 0 && answered.length !== suggested.length) {
    return true;
  }

  const ans = answered.map((d) => ({
    ...d,
    startYear: new Date(d.startDate).getFullYear(),
    endYear: new Date(d.endDate).getFullYear(),
  })).sort((a, b) => b.startYear - a.startYear);
  const sug = suggested.map((d) => ({
    ...d,
    startYear: new Date(d.startDate).getFullYear(),
    endYear: new Date(d.endDate).getFullYear(),
  })).sort((a, b) => b.startYear - a.startYear);

  let k = 0;
  let hasChanged = false;
  while (k < sug.length && !hasChanged) {
    if (
      (sug[k].startYear !== ans[k].startYear)
      || (sug[k].endYear !== ans[k].endYear)
      || (sug[k].title !== ans[k].title)
      || (sug[k].companyName !== ans[k].companyName)
    ) {
      hasChanged = true;
    }
    k += 1;
  }

  return hasChanged;
};

const areEducationChanged = (answered, suggested) => {
  // There are no suggested past roles, so nothing to udpate
  if (suggested.length === 0) {
    return false;
  }

  // Past roles may be overriden because there are some suggestions
  if (suggested.length > 0 && answered.length !== suggested.length) {
    return true;
  }

  const ans = answered.sort((a, b) => b.startYear - a.startYear);
  const sug = suggested.map((d) => ({
    ...d,
    startYear: new Date(d.startDate).getFullYear(),
    endYear: new Date(d.endDate).getFullYear(),
  })).sort((a, b) => b.startYear - a.startYear);

  let k = 0;
  let hasChanged = false;
  while (k < sug.length && !hasChanged) {
    if (
      (sug[k].areaOfStudy !== ans[k].areaOfStudy)
      // (sug[k]["degree"] !== ans[k]["degreeId"]) ||
      || (sug[k].startYear !== ans[k].startYear)
      || (sug[k].endYear !== ans[k].endYear)
      || (sug[k].school !== ans[k].school)
    ) {
      hasChanged = true;
    }
    k += 1;
  }

  return hasChanged;
};

const mergingResumeAndLinkedInSuggestions = (profileSuggestions) => {
  const { resume, linkedin } = profileSuggestions;
  const merged = {
    location: {
      accepted: true,
      location: null,
    },
    bio: {
      accepted: true,
      bio: null,
    },
    education: [],
    pastRoles: [],
    functionalAreas: [],
    industries: [],
    productTechnologies: [],
    therapeuticAreas: [],
    stagesOfDevelopments: [],
  };

  if (resume.location) {
    merged.location.location = resume.location;
  }
  if (resume.bio || resume.summary) {
    merged.bio.bio = resume.bio || resume.summary;
  }
  merged.pastRoles = resume.employments.sort(
    (a, b) => (new Date(b.startDate) - new Date(a.startDate)),
  );
  merged.education = resume.education.sort(
    (a, b) => (new Date(b.startDate) - new Date(a.startDate)),
  );
  merged.functionalAreas = linkedin.functionalAreas;
  merged.industries = linkedin.industries;
  merged.productTechnologies = linkedin.productTechnologies;
  merged.therapeuticAreas = linkedin.therapeuticAreas;
  merged.stagesOfDevelopments = linkedin.stagesOfDevelopment;

  return parsingPostTreatment(merged);
};

const profileSuggestionsReducer = (state = initialState, action) => {
  let { data } = action;
  const { profileSuggestions, answers, hasChanged } = state;
  const { merged } = profileSuggestions;
  let { updated } = profileSuggestions;
  let errors = {};
  let newMerged;
  let suggestions;
  let changed;
  let existingAnswers;
  let bioHasChanged;
  let locationHasChanged;
  let pastRolesHasChanged;
  let educationHasChanged;

  switch (action.type) {
    case 'REINITIALIZE_RESUME':
      profileSuggestions.resume = initialResume;
      newMerged = mergingResumeAndLinkedInSuggestions(profileSuggestions);

      return {
        ...state,
        profileSuggestions: {
          ...profileSuggestions,
          merged: newMerged,
        },
      };
    case 'RESUME_PARSED':
      data = _.mapKeys(data, (v, k) => _.camelCase(k));
      profileSuggestions.resume = data;
      updated = true;

      newMerged = mergingResumeAndLinkedInSuggestions(profileSuggestions);
      errors = checkData(newMerged);

      return {
        ...state,
        profileSuggestions: {
          ...profileSuggestions,
          updated,
          merged: newMerged,
        },
        errors,
      };
    case 'LINKEDIN_PROFILE_PARSED':
      data = _.mapKeys(data, (v, k) => _.camelCase(k));
      profileSuggestions.linkedin = data;
      updated = true;

      newMerged = mergingResumeAndLinkedInSuggestions(profileSuggestions);
      errors = checkData(newMerged);

      return {
        ...state,
        profileSuggestions: {
          ...profileSuggestions,
          updated,
          merged: newMerged,
        },
        errors,
      };
    case 'PROFILE_SUGGESTIONS_PARSED':
      data = keysToCamel(data);
      suggestions = data.suggestions;
      changed = data.changed;
      existingAnswers = data.answers;

      merged.location.location = suggestions.location;
      merged.bio.bio = suggestions.bio || suggestions.summary;
      merged.pastRoles = suggestions.pastRoles;
      merged.education = suggestions.education;
      merged.functionalAreas = suggestions.functionalAreas;
      merged.industries = suggestions.industries;
      merged.productTechnologies = suggestions.productTechnologies;
      merged.therapeuticAreas = suggestions.therapeuticAreas;
      merged.stagesOfDevelopments = suggestions.stagesOfDevelopments;
      updated = true;

      newMerged = parsingPostTreatment(merged);
      errors = checkData(newMerged);

      return {
        ...state,
        profileSuggestions: {
          ...profileSuggestions,
          updated,
          merged: newMerged,
        },
        errors,
        hasChanged: changed,
        answers: existingAnswers,
      };
    case 'UPDATE_BIO':
      bioHasChanged = hasChanged.bio;
      if (!data.accepted) {
        bioHasChanged = false;
      } else if (answers.bio === data.bio) {
        bioHasChanged = false;
      } else {
        bioHasChanged = true;
      }

      return {
        ...state,
        profileSuggestions: {
          ...profileSuggestions,
          merged: {
            ...merged,
            bio: data,
          },
        },
        hasChanged: {
          ...hasChanged,
          bio: bioHasChanged,
        },
      };
    case 'UPDATE_LOCATION':
      locationHasChanged = hasChanged.location;
      if (!data.accepted) {
        locationHasChanged = false;
      } else if (answers.bio === data.location) {
        locationHasChanged = false;
      } else {
        locationHasChanged = true;
      }

      return {
        ...state,
        profileSuggestions: {
          ...profileSuggestions,
          merged: {
            ...merged,
            location: data,
          },
        },
        hasChanged: {
          ...hasChanged,
          location: locationHasChanged,
        },
      };
    case 'UPDATE_ARRAY':
      newMerged = {
        ...merged,
        [data.id]: data.newArray,
      };
      errors = checkData(newMerged);
      pastRolesHasChanged = hasChanged.pastRoles;
      educationHasChanged = hasChanged.education;

      if (data.id === 'education') {
        const acceptedOnes = data.newArray
          .filter((d) => d.accepted)
          .map((d) => d.education)
          .flat();

        educationHasChanged = areEducationChanged(answers.education, acceptedOnes);
      }
      if (data.id === 'pastRoles') {
        const acceptedOnes = data.newArray
          .filter((d) => d.accepted)
          .map((d) => d.pastRole)
          .flat();

        pastRolesHasChanged = arePastRolesChanged(answers.pastRoles, acceptedOnes);
      }

      return {
        ...state,
        profileSuggestions: {
          ...profileSuggestions,
          merged: newMerged,
        },
        errors,
        hasChanged: {
          ...hasChanged,
          education: educationHasChanged,
          pastRoles: pastRolesHasChanged,
        },
      };
    default:
      return state;
  }
};

export default profileSuggestionsReducer;
